import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Badge } from '@/components/badge';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { useMyProjects } from '@/lib/projects';
import {
  PROJECT_TYPE_LABEL,
  PROJECT_STATUS_LABEL,
  PROJECT_STATUS_TONE,
  type ProjectStatus,
} from '@/lib/project-types';
import { formatStatus } from '@/lib/format';
import type { ProjectData } from '@/types/api';

/**
 * Client-facing projects list. Shows the client's own work with progress at a
 * glance — no "new project" (admins create those) and no cross-client framing.
 */
export function PortalProjects() {
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useMyProjects(page);
  const data = paginated?.data;
  const meta = paginated?.meta;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FolderKanban}
        title="Projects"
        subtitle="Track the status and progress of your work with the Mehrutherm team."
      />

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No active projects"
          description="When the Mehrutherm team starts a project for you, you'll be able to follow its progress here."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function ProjectCard({ project }: { project: ProjectData }) {
  const pct = project.cards_count > 0 ? Math.round((project.cards_done / project.cards_count) * 100) : 0;

  return (
    <Link to={`/projects/${project.id}`}>
      <Card className="h-full transition-colors hover:bg-[var(--color-surface-muted)]/40">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 font-medium">{project.name}</div>
            <Badge tone={PROJECT_STATUS_TONE[project.status as ProjectStatus] ?? 'neutral'}>
              {PROJECT_STATUS_LABEL[project.status as ProjectStatus] ?? formatStatus(project.status)}
            </Badge>
          </div>
          <div className="mt-1">
            <Badge tone="neutral">{PROJECT_TYPE_LABEL[project.type] ?? project.type}</Badge>
          </div>
        </CardHeader>
        <CardBody className="space-y-2">
          <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-surface-muted)]">
            <div
              className="h-full rounded-full bg-[var(--color-primary)] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-[var(--color-foreground-muted)]">
            <span>
              {project.cards_done} of {project.cards_count} done
            </span>
            <span>{pct}%</span>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
